import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import ManagerSidebar from '../manager-dashboard/components/ManagerSidebar';
import ManagerBottomNav from '../manager-dashboard/components/ManagerBottomNav';
import ManagerMobileHeader from '../manager-dashboard/components/ManagerMobileHeader';

const SERVERS = ['MENA', 'FR', 'IT', 'DE', 'US'];
const PAGE_SIZE = 25;

const APPEAL_BADGES = {
  'Not appealed': { bg: 'rgba(249,115,22,0.12)', color: '#f97316', border: 'rgba(249,115,22,0.3)' },
  'Not approved': { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', border: 'rgba(239,68,68,0.3)' },
  'Not approved, open to inquiry': { bg: 'rgba(220,38,38,0.12)', color: '#dc2626', border: 'rgba(220,38,38,0.3)' },
  'Ticket opened': { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6', border: 'rgba(59,130,246,0.3)' },
  'Under review': { bg: 'rgba(168,85,247,0.12)', color: '#a855f7', border: 'rgba(168,85,247,0.3)' },
  'Approved': { bg: 'rgba(34,197,94,0.12)', color: '#22c55e', border: 'rgba(34,197,94,0.3)' },
};

const PENALTY_BADGES = {
  'In effect': { bg: 'rgba(239,68,68,0.12)', color: '#ef4444' },
  'Ended': { bg: 'rgba(249,115,22,0.12)', color: '#f97316' },
  'Withdrawn': { bg: 'rgba(34,197,94,0.12)', color: '#22c55e' },
};

const PENALTY_TYPE_BADGES = {
  'LIVE access suspended': { bg: 'rgba(239,68,68,0.08)', color: '#f87171', label: 'LIVE' },
  'Multi-guest suspended': { bg: 'rgba(249,115,22,0.08)', color: '#fb923c', label: 'Multi-guest' },
  'Account suspended': { bg: 'rgba(168,85,247,0.08)', color: '#a855f7', label: 'Account' },
};

const Badge = ({ text, config }) => {
  const c = config || { bg: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)' };
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: c.bg, color: c.color, border: c.border ? `1px solid ${c.border}` : undefined, fontFamily: 'Inter, sans-serif' }}>
      {text}
    </span>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="rounded-xl border p-4" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
    <p className="text-xs uppercase tracking-wider mb-1" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{label}</p>
    <p className="text-2xl font-bold" style={{ color, fontFamily: 'Sora, sans-serif' }}>{value}</p>
  </div>
);

const formatDate = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// ─── Detail Panel ────────────────────────────────────────────────────────────

const ViolationDetail = ({ violation, onClose, t }) => {
  const [history, setHistory] = useState([]);
  const [allViolations, setAllViolations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [histRes, allRes] = await Promise.all([
        supabase.from('violations_status_history').select('*').eq('punish_record_id', violation.punish_record_id).order('recorded_at', { ascending: true }),
        supabase.from('violations').select('*').eq('tiktok_id', violation.tiktok_id).order('punishment_time', { ascending: false }),
      ]);
      setHistory(histRes.data || []);
      setAllViolations(allRes.data || []);
      setLoading(false);
    };
    load();
  }, [violation]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-lg h-full overflow-y-auto" style={{ backgroundColor: '#0a0a0a', borderLeft: '1px solid #1f1f1f' }} onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 px-5 py-4 flex items-center justify-between border-b" style={{ backgroundColor: '#0a0a0a', borderColor: '#1f1f1f' }}>
          <div>
            <h2 className="text-base font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{violation.creator_nickname || violation.creator_username}</h2>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>@{violation.creator_username} · {violation.tiktok_id} · {violation.server}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.5)' }}>✕</button>
        </div>
        <div className="p-5 space-y-5">
          <div className="rounded-xl border p-4 space-y-3" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge text={violation.penalty_status} config={PENALTY_BADGES[violation.penalty_status]} />
              <Badge text={violation.appeal_status} config={APPEAL_BADGES[violation.appeal_status]} />
              {violation.is_high_severity && <span className="text-xs" style={{ color: '#ef4444' }}>⚠️ High severity</span>}
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter, sans-serif' }}>
              <div><span style={{ color: 'rgba(255,255,255,0.25)' }}>{t('violations.detail.type')}</span><p className="text-white mt-0.5">{violation.violation_type}</p></div>
              <div><span style={{ color: 'rgba(255,255,255,0.25)' }}>{t('violations.detail.content')}</span><p className="text-white mt-0.5">{violation.violation_content || '-'}</p></div>
              <div><span style={{ color: 'rgba(255,255,255,0.25)' }}>{t('violations.detail.dateViolation')}</span><p className="text-white mt-0.5">{formatDate(violation.punishment_time)}</p></div>
              <div><span style={{ color: 'rgba(255,255,255,0.25)' }}>{t('violations.detail.endPenalty')}</span><p className="text-white mt-0.5">{formatDate(violation.recovery_time)}</p></div>
              <div><span style={{ color: 'rgba(255,255,255,0.25)' }}>{t('violations.detail.recordId')}</span><p className="text-white mt-0.5 font-mono text-xs">{violation.punish_record_id}</p></div>
              <div><span style={{ color: 'rgba(255,255,255,0.25)' }}>{t('violations.detail.ticketId')}</span><p className="text-white mt-0.5 font-mono text-xs">{violation.ticket_id === '0' ? '-' : violation.ticket_id}</p></div>
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>{t('violations.detail.timeline')}</h3>
            {loading ? (
              <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 rounded animate-pulse" style={{ backgroundColor: '#1a1a1a' }} />)}</div>
            ) : history.length === 0 ? (
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('violations.noStatusChange')}</p>
            ) : (
              <div className="space-y-0 border-l-2" style={{ borderColor: '#2a2a2a', marginLeft: '8px' }}>
                {history.map((h, i) => (
                  <div key={h.id} className="pl-5 pb-4 relative">
                    <div className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full" style={{ backgroundColor: i === history.length - 1 ? '#E5202A' : '#3b82f6' }} />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>{formatDate(h.recorded_at)} — {h.triggered_by}</p>
                    <p className="text-sm text-white mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {h.old_status
                        ? h.old_status === 'Ticket opened' && h.new_status !== 'Approved' && h.new_status !== 'Ticket opened'
                          ? `Ticket ouvert par le bot (statut API: ${h.new_status})`
                          : `${h.old_status} → ${h.new_status}`
                        : `Détectée: ${h.new_status}`}
                      {h.old_penalty && h.old_penalty !== h.new_penalty && <span style={{ color: 'rgba(255,255,255,0.4)' }}> · Penalty: {h.old_penalty} → {h.new_penalty}</span>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Sora, sans-serif' }}>
              {t('violations.detail.creatorHistory', { count: allViolations.length })}
            </h3>
            <div className="space-y-2">
              {allViolations.map(v => (
                <div key={v.id} className="rounded-lg border p-3 flex items-center justify-between"
                  style={{ backgroundColor: v.id === violation.id ? 'rgba(229,32,42,0.06)' : '#111111', borderColor: v.id === violation.id ? 'rgba(229,32,42,0.2)' : '#1f1f1f' }}>
                  <div className="min-w-0">
                    <p className="text-xs text-white truncate" style={{ fontFamily: 'Inter' }}>{v.violation_type}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.3)' }}>{formatDate(v.punishment_time)}</p>
                  </div>
                  <div className="flex gap-1.5 flex-shrink-0 ml-2">
                    <Badge text={v.penalty_status} config={PENALTY_BADGES[v.penalty_status]} />
                    <Badge text={v.appeal_status} config={APPEAL_BADGES[v.appeal_status]} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const ManagerViolations = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [managerProfile, setManagerProfile] = useState(null);
  const [managerTiktokIds, setManagerTiktokIds] = useState([]);

  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, inEffect: 0, notAppealed: 0, openToInquiry: 0, ticketOpened: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  const [serverFilter, setServerFilter] = useState('');
  const [appealFilter, setAppealFilter] = useState('');
  const [penaltyFilter, setPenaltyFilter] = useState('');
  const [penaltyTypeFilter, setPenaltyTypeFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [highSeverityOnly, setHighSeverityOnly] = useState(false);
  const [search, setSearch] = useState('');

  const [selectedViolation, setSelectedViolation] = useState(null);

  // Generate last 6 months options
  const monthOptions = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    monthOptions.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate('/login-authentication'); return; }
      const { data: profile } = await supabase.from('users').select('*').eq('id', user.id).single();
      setManagerProfile(profile);

      // Get manager's creator tiktok_ids
      const { data: ccm } = await supabase.from('creator_current_month').select('tiktok_id').eq('manager_id', user.id);
      const ids = (ccm || []).map(r => r.tiktok_id).filter(Boolean);
      setManagerTiktokIds(ids);
    };
    init();
  }, []);

  const fetchData = useCallback(async () => {
    if (managerTiktokIds.length === 0) { setLoading(false); return; }
    setLoading(true);
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let query = supabase.from('violations').select('*', { count: 'exact' }).in('tiktok_id', managerTiktokIds).order('punishment_time', { ascending: false }).range(from, to);
      if (serverFilter) query = query.eq('server', serverFilter);
      if (appealFilter) query = query.eq('appeal_status', appealFilter);
      if (penaltyFilter) query = query.eq('penalty_status', penaltyFilter);
      if (penaltyTypeFilter) query = query.eq('penalty_type', penaltyTypeFilter);
      if (highSeverityOnly) query = query.eq('is_high_severity', true);
      if (search) query = query.or(`creator_username.ilike.%${search}%,creator_nickname.ilike.%${search}%,tiktok_id.ilike.%${search}%`);
      if (monthFilter) {
        const mStart = `${monthFilter}-01T00:00:00`;
        const parts = monthFilter.split('-');
        const nextMonth = new Date(parseInt(parts[0]), parseInt(parts[1]), 1);
        const mEnd = nextMonth.toISOString();
        query = query.gte('punishment_time', mStart).lt('punishment_time', mEnd);
      }
      const { data: vData, count } = await query;
      setViolations(vData || []);
      setTotalCount(count || 0);

      // Stats
      const sq = (extra) => {
        let q = supabase.from('violations').select('id', { count: 'exact', head: true }).in('tiktok_id', managerTiktokIds);
        if (serverFilter) q = q.eq('server', serverFilter);
        if (monthFilter) {
          const ms = `${monthFilter}-01T00:00:00`;
          const mp = monthFilter.split('-');
          const mn = new Date(parseInt(mp[0]), parseInt(mp[1]), 1);
          q = q.gte('punishment_time', ms).lt('punishment_time', mn.toISOString());
        }
        return extra ? extra(q) : q;
      };
      const [totalRes, inEffectRes, notAppealedRes, openInquiryRes, ticketRes] = await Promise.all([
        sq(),
        sq(q => q.eq('penalty_status', 'In effect')),
        sq(q => q.eq('appeal_status', 'Not appealed').eq('penalty_status', 'In effect')),
        sq(q => q.eq('appeal_status', 'Not approved, open to inquiry')),
        sq(q => q.eq('appeal_status', 'Ticket opened')),
      ]);
      setStats({
        total: totalRes.count || 0,
        inEffect: inEffectRes.count || 0,
        notAppealed: notAppealedRes.count || 0,
        openToInquiry: openInquiryRes.count || 0,
        ticketOpened: ticketRes.count || 0,
      });
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [managerTiktokIds, serverFilter, appealFilter, penaltyFilter, penaltyTypeFilter, monthFilter, highSeverityOnly, search, page]);

  useEffect(() => { if (managerTiktokIds.length > 0) fetchData(); }, [fetchData, managerTiktokIds]);

  useEffect(() => { setPage(1); }, [serverFilter, appealFilter, penaltyFilter, penaltyTypeFilter, monthFilter, highSeverityOnly, search]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <ManagerSidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} managerName={managerProfile?.name || ''} />
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0" style={{ backgroundColor: 'var(--color-background)' }}>
        <ManagerMobileHeader title={t('violations.title')} />

        <div className="sticky top-0 z-10 px-4 md:px-6 py-4 flex flex-wrap items-center gap-3 border-b" style={{ backgroundColor: 'var(--color-background)', borderColor: '#1a1a1a' }}>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>⚠️ {t('violations.title')}</h1>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
              {t('violations.subtitle')}
            </p>
          </div>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard label={t('violations.stats.total')} value={stats.total} color="rgba(255,255,255,0.7)" />
            <StatCard label={t('violations.stats.inEffect')} value={stats.inEffect} color="#ef4444" />
            <StatCard label={t('violations.stats.notAppealed')} value={stats.notAppealed} color="#f97316" />
            <StatCard label={t('violations.stats.readyForTicket')} value={stats.openToInquiry} color="#dc2626" />
            <StatCard label={t('violations.stats.ticketOpened')} value={stats.ticketOpened} color="#3b82f6" />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex gap-1 rounded-lg p-1" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a' }}>
              <button onClick={() => setServerFilter('')} className="px-3 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: !serverFilter ? '#E5202A' : 'transparent', color: !serverFilter ? '#fff' : 'rgba(255,255,255,0.5)', fontFamily: 'Inter' }}>{t('common.all')}</button>
              {SERVERS.map(s => (
                <button key={s} onClick={() => setServerFilter(s)} className="px-3 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: serverFilter === s ? '#E5202A' : 'transparent', color: serverFilter === s ? '#fff' : 'rgba(255,255,255,0.5)', fontFamily: 'Inter' }}>{s}</button>
              ))}
            </div>

            <select value={monthFilter} onChange={e => setMonthFilter(e.target.value)} className="text-base rounded-lg px-3 py-2 outline-none" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a', color: monthFilter ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', fontFamily: 'Inter' }}>
              <option value="">{t('violations.allMonths')}</option>
              {monthOptions.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

            <select value={appealFilter} onChange={e => setAppealFilter(e.target.value)} className="text-base rounded-lg px-3 py-2 outline-none" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a', color: appealFilter ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', fontFamily: 'Inter' }}>
              <option value="">{t('violations.allAppeals')}</option>
              <option value="Not appealed">{t('violations.appealStatus.notAppealed', 'Not appealed')}</option>
              <option value="Not approved">{t('violations.appealStatus.notApproved', 'Not approved')}</option>
              <option value="Not approved, open to inquiry">{t('violations.appealStatus.openToInquiry', 'Open to inquiry')}</option>
              <option value="Ticket opened">{t('violations.appealStatus.ticketOpened', 'Ticket opened')}</option>
              <option value="Under review">{t('violations.appealStatus.underReview', 'Under review')}</option>
              <option value="Approved">{t('violations.appealStatus.approved', 'Approved')}</option>
            </select>

            <select value={penaltyFilter} onChange={e => setPenaltyFilter(e.target.value)} className="text-base rounded-lg px-3 py-2 outline-none" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a', color: penaltyFilter ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', fontFamily: 'Inter' }}>
              <option value="">{t('violations.allPenalties')}</option>
              <option value="In effect">{t('violations.penaltyStatus.inEffect', 'In effect')}</option>
              <option value="Ended">{t('violations.penaltyStatus.ended', 'Ended')}</option>
              <option value="Withdrawn">{t('violations.penaltyStatus.withdrawn', 'Withdrawn')}</option>
            </select>

            <select value={penaltyTypeFilter} onChange={e => setPenaltyTypeFilter(e.target.value)} className="text-base rounded-lg px-3 py-2 outline-none" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a', color: penaltyTypeFilter ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)', fontFamily: 'Inter' }}>
              <option value="">{t('violations.allTypes')}</option>
              <option value="LIVE access suspended">LIVE suspended</option>
              <option value="Multi-guest suspended">Multi-guest</option>
              <option value="Account suspended">Account suspended</option>
            </select>

            <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: highSeverityOnly ? '#ef4444' : 'rgba(255,255,255,0.4)', fontFamily: 'Inter' }}>
              <input type="checkbox" checked={highSeverityOnly} onChange={e => setHighSeverityOnly(e.target.checked)} style={{ accentColor: '#ef4444' }} />
              ⚠️ High severity
            </label>

            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('violations.search')}
              className="text-base rounded-lg px-3 py-2 outline-none ml-auto" style={{ backgroundColor: '#111111', border: '1px solid #2a2a2a', color: 'rgba(255,255,255,0.8)', fontFamily: 'Inter', width: '180px' }} />
          </div>

          {/* Table */}
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#111111', borderColor: '#1f1f1f' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                    {[t('violations.table.creator'), 'TikTok ID', t('violations.table.server'), t('violations.table.type'), t('violations.table.penaltyType'), t('violations.table.penalty'), t('violations.table.appeal'), t('violations.table.date'), t('violations.table.end'), ''].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(8)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #1a1a1a' }}>
                        {[1,2,3,4,5,6,7,8,9,10].map(j => <td key={j} className="px-4 py-3"><div className="h-3 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: j === 1 ? '120px' : '60px' }} /></td>)}
                      </tr>
                    ))
                  ) : violations.length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-12 text-center text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('violations.noViolation')}</td></tr>
                  ) : violations.map(v => (
                    <tr key={v.id} onClick={() => setSelectedViolation(v)} className="cursor-pointer transition-colors" style={{ borderBottom: '1px solid #1a1a1a' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td className="px-4 py-3">
                        <p className="text-sm text-white" style={{ fontFamily: 'Inter' }}>{v.creator_username}</p>
                        {v.creator_nickname && <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{v.creator_nickname}</p>}
                      </td>
                      <td className="px-4 py-3"><span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>{v.tiktok_id}</span></td>
                      <td className="px-4 py-3"><span className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Sora' }}>{v.server}</span></td>
                      <td className="px-4 py-3"><p className="text-xs text-white truncate max-w-[150px]" style={{ fontFamily: 'Inter' }}>{v.violation_type}</p></td>
                      <td className="px-4 py-3">
                        {v.penalty_type ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                            style={{ backgroundColor: (PENALTY_TYPE_BADGES[v.penalty_type] || {}).bg || 'rgba(255,255,255,0.06)', color: (PENALTY_TYPE_BADGES[v.penalty_type] || {}).color || 'rgba(255,255,255,0.4)', fontFamily: 'Inter' }}>
                            {(PENALTY_TYPE_BADGES[v.penalty_type] || {}).label || v.penalty_type}
                          </span>
                        ) : <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>}
                      </td>
                      <td className="px-4 py-3"><Badge text={v.penalty_status} config={PENALTY_BADGES[v.penalty_status]} /></td>
                      <td className="px-4 py-3"><Badge text={v.appeal_status} config={APPEAL_BADGES[v.appeal_status]} /></td>
                      <td className="px-4 py-3"><span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter' }}>{formatDate(v.punishment_time)}</span></td>
                      <td className="px-4 py-3"><span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'Inter' }}>{formatDate(v.recovery_time)}</span></td>
                      <td className="px-4 py-3">{v.is_high_severity && <span className="text-sm">⚠️</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t flex items-center justify-between" style={{ borderColor: '#1f1f1f' }}>
              <span className="text-xs" style={{ color: 'rgba(255,255,255,0.25)', fontFamily: 'Inter' }}>
                {t('violations.count', { count: totalCount })} · {t('violations.page', { page, total: totalPages || 1 })}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1 rounded text-xs font-medium disabled:opacity-30"
                  style={{ backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter' }}>
                  {t('common.prev')}
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                  className="px-3 py-1 rounded text-xs font-medium disabled:opacity-30"
                  style={{ backgroundColor: '#1a1a1a', color: 'rgba(255,255,255,0.6)', fontFamily: 'Inter' }}>
                  {t('common.next')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <ManagerBottomNav />
      {selectedViolation && <ViolationDetail violation={selectedViolation} onClose={() => setSelectedViolation(null)} t={t} />}
    </div>
  );
};

export default ManagerViolations;
